import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/ui/Button';
import { Badge } from '@/ui/badge';
import { Switch } from '@/ui/switch';
import { GripVertical, Plus, Trash2, Edit } from 'lucide-react';
import { supabaseService, type RepositoryRule, type CustomRule } from '@/shared/services/supabaseService';
import { toast } from 'sonner';
import RuleSelector from './RuleSelector';
import CreateRuleDialog from './CreateRuleDialog';
import { Skeleton } from '@/shared/components';

interface RepositoryRulesManagerProps {
  repositoryId: string;
  onRulesChange?: (rules: RepositoryRule[]) => void;
}

const RepositoryRulesManager: React.FC<RepositoryRulesManagerProps> = ({
  repositoryId,
  onRulesChange
}) => {
  const [rules, setRules] = useState<RepositoryRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<CustomRule | undefined>();

  const fetchRules = useCallback(async () => {
    try {
      const repositoryRules = await supabaseService.getRepositoryRules(repositoryId);
      setRules(repositoryRules);
      if (onRulesChange) {
        onRulesChange(repositoryRules);
      }
    } catch (error) {
      console.error('Error fetching repository rules:', error);
      toast.error('Failed to load repository rules');
    } finally {
      setLoading(false);
    }
  }, [repositoryId, onRulesChange]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleAddRule = async (rule: CustomRule) => {
    try {
      await supabaseService.addRuleToRepository(repositoryId, rule.id);
      toast.success('Rule added to repository');
      fetchRules();
    } catch (error) {
      console.error('Error adding rule to repository:', error);
      toast.error('Failed to add rule to repository');
    }
  };

  const handleRemoveRule = async (ruleId: string) => {
    try {
      await supabaseService.removeRuleFromRepository(repositoryId, ruleId);
      toast.success('Rule removed from repository');
      fetchRules();
    } catch (error) {
      console.error('Error removing rule from repository:', error);
      toast.error('Failed to remove rule from repository');
    }
  };

  const handleToggleRule = async (ruleId: string, enabled: boolean) => {
    try {
      await supabaseService.toggleRepositoryRule(repositoryId, ruleId, enabled);
      toast.success(`Rule ${enabled ? 'enabled' : 'disabled'}`);
      fetchRules();
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Failed to update rule status');
    }
  };

  const handleCreateRule = async (rule: CustomRule) => {
    // Add the newly created rule to the repository
    await handleAddRule(rule);
    setShowCreateDialog(false);
  };

  const handleEditRule = (rule: CustomRule) => {
    setEditingRule(rule);
  };

  const handleUpdateRule = async () => {
    setEditingRule(undefined);
    fetchRules(); // Refresh the rules list
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-none" />
        <Skeleton className="h-32 w-full rounded-none" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border border-neutral-200 dark:border-neutral-800 p-4 bg-neutral-50 dark:bg-neutral-900/10">
            <div>
              <h3 className="font-mono text-lg font-bold uppercase">Active Rules</h3>
              <p className="font-mono text-xs text-muted-foreground mt-1">
                Rules applied to this repository during code review.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateDialog(true)}
                className="rounded-none font-mono uppercase text-xs border-neutral-950 dark:border-neutral-100"
              >
                <Plus className="h-3 w-3 mr-2" />
                CREATE_RULE
              </Button>
              <RuleSelector
                onSelect={handleAddRule}
                excludeIds={rules.map(r => r.custom_rule_id)}
              />
            </div>
        </div>

        <div className="border border-neutral-200 dark:border-neutral-800 bg-background">
          {rules.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="font-mono text-sm font-bold uppercase text-muted-foreground mb-2">NO_RULES_CONFIGURED</p>
              <p className="font-mono text-xs text-muted-foreground">
                  Add rules to enforce standards in this repository.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {rules.map((rule, index) => (
                <div
                  key={rule.id}
                  className="flex items-start gap-4 p-4 hover:bg-neutral-50 dark:hover:bg-neutral-900/20 transition-colors group"
                >
                  <div className="flex items-center gap-2 mt-1">
                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab opacity-50 group-hover:opacity-100" />
                    <span className="font-mono text-xs text-muted-foreground w-6 text-center">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-mono font-bold text-sm uppercase truncate">{rule.title}</h4>
                      {rule.is_public && (
                        <Badge variant="secondary" className="rounded-none text-[10px] px-1 font-mono uppercase h-4 border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-black">
                          Public
                        </Badge>
                      )}
                    </div>
                    <p className="font-mono text-xs text-muted-foreground line-clamp-2 bg-neutral-100 dark:bg-neutral-900 p-2 border border-neutral-100 dark:border-neutral-800">
                      {rule.rule_content}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={rule.is_enabled}
                      onCheckedChange={(enabled) => handleToggleRule(rule.custom_rule_id, enabled)}
                      className="data-[state=checked]:bg-green-600 dark:data-[state=checked]:bg-green-500 border-neutral-950 dark:border-neutral-100 mr-2"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditRule({
                        id: rule.custom_rule_id,
                        user_id: '',
                        title: rule.title,
                        description: rule.description || '',
                        rule_content: rule.rule_content,
                        is_public: rule.is_public,
                        usage_count: 0,
                        created_at: '',
                        updated_at: ''
                      })}
                      className="h-8 w-8 rounded-none hover:bg-neutral-200 dark:hover:bg-neutral-800"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveRule(rule.custom_rule_id)}
                      className="h-8 w-8 rounded-none hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* Create Rule Dialog */}
      <CreateRuleDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateRule}
      />

      {/* Edit Rule Dialog */}
      <CreateRuleDialog
        isOpen={!!editingRule}
        onClose={() => setEditingRule(undefined)}
        onSuccess={handleUpdateRule}
        editingRule={editingRule}
      />
    </div>
  );
};

export default RepositoryRulesManager;
